import { useLocation } from "react-router-dom";
import { memoryStore, useStore } from "../../../lib/store";
import type { SignupSurface } from "../../../tools/analytics/domains/onboarding.analytics";

/**
 * The code the desktop sign-in page has just asked for, between the page that
 * asks and the modal that waits for it.
 *
 * The desktop door is two screens (Figma "D7.inscription.welcome" or
 * "D7.connexion", then "D7.inscription.default"): the address on `/signup` or
 * `/login`, then the six boxes in a
 * modal over the app the visitor is signing into. The page decides the intent
 * (`check-email`), the modal requests and verifies the code under it. This is
 * what one hands to the other.
 *
 * It carries the intent because the API branches on it and a code is burned on
 * its first verify: a login code never opens a new account, a signup code never
 * opens an existing one.
 *
 * IN MEMORY: a reload drops it, and the logged-out guard then sends the visitor
 * back to `/signup` for a fresh code, which is the right answer to a code nobody
 * can now verify.
 *
 * BOUND TO THE PAGE BEHIND IT (`behindPathname`): it stands only over the page
 * the door opened, and a visitor who goes on to any other page has left it.
 * Arriving at a door clears it (`useDesktopLogin`), and so does logout, for the
 * next person on the tab: it never comes back over a private page with their
 * address and a code mailed to it.
 */
export type DesktopCodeRequest = {
  email: string;
  behindPathname: string;
  intent: "login" | "signup";
  surface: Extract<
    SignupSurface,
    "desktop_gate" | "anonymous_gpt_wall" | "anonymous_gpt_member"
  >;
};

export const desktopCodeRequestStore = memoryStore<DesktopCodeRequest | null>(
  null
);

/** The request that stands over the current page, or `null`. */
export function useDesktopCodeRequest(): DesktopCodeRequest | null {
  const [value] = useStore(desktopCodeRequestStore);
  const location = useLocation();
  return value !== null && value.behindPathname === location.pathname
    ? value
    : null;
}
